import { UnicornDetectorIcon } from '@mm/icons/AntDesignIcons';
import { useAutomationActions } from '@mm/state/actions/automationActions';
import { usePageCRUDActions } from '@mm/state/actions/PageCRUDActions';
import { useWidgetCRUDActions } from '@mm/state/actions/WidgetCRUDActions';
import {
  EAutomationAction,
  IAutomationAction as IActionsList,
} from '@mm/state/model/AutomationModel';
import WidgetModel, { WidgetTypes } from '@mm/state/model/WidgetModel';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import {
  getAutomationAction,
  getGraphLibInstance,
} from '@mm/state/selectors/automations';
import { activeFacebookPageSelector } from '@mm/state/selectors/facebookPageSelector';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import { ReactComponent as GoogleSheetsIcon } from 'assets/google-sheets-logo.svg';
import { ReactComponent as ZapierIcon } from 'assets/icons/zapier-logomark.svg';
import { ReactComponent as MailchimpIcon } from 'assets/mailchimp.svg';
import { ReactComponent as WebhookIcon } from 'assets/webhook-icon.svg';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { timeout } from '../../../../../../common/helpers/utils';
import { useConnectionWidgetHandlers } from '../../../../../../common/helpers/widget-helpers/use-connection-widget-handlers';
import { useDelayWidgetHandlers } from '../../../../../../common/helpers/widget-helpers/use-delay-widget-handlers';
import { useNavigateWidgetHandlers } from '../../../../../../common/helpers/widget-helpers/use-navigate-widget-handlers';
import { usePhoneNumber } from '../../../../../../common/hooks/use-phone-number';
import { useFreeTrialUpgradeModal } from '../../../../../../common/modals/FreeTrialUpgradeModal/use-free-trial-upgrade-modal';
import { ConditionIcon } from './DialogueEditor/NodeTypes/Condition/Condition';
import { DelayIcon } from './DialogueEditor/NodeTypes/Delay/Delay';
import {
  EmailDialogueIcon,
  SMSDialogueIcon,
} from './DialogueEditor/NodeTypes/Dialogue/Dialogue';

export interface IAutomationAction {
  type: WidgetTypes;
  id: EAutomationAction;
  name: string;
  description?: string;
  add?: (
    dialogueId: number,
    sortOrder: number,
    pageGroupId?: number,
    widgetId?: number,
    position?: 'before' | 'after',
    isUnicornPath?: boolean,
  ) => Promise<WidgetModel>;
  icon: typeof ConditionIcon | typeof UnicornDetectorIcon;
  iconBgColor?: string;
  disabled?: boolean;
  iconColor?: string;
  className?: string;
}

export const useAutomationActionList = (
  props: IActionsList & {
    campaignId: number;
    visibleActionsFilterFunc?: (id: EAutomationAction) => boolean;
  },
): IAutomationAction[] => {
  const { campaignId, visibleActionsFilterFunc: byVisibility = () => true } =
    props;
  const botId = useSelector(activeChatbotIdSelector);
  const navigateWidgetHandlers = useNavigateWidgetHandlers();
  const delayWidgetHandlers = useDelayWidgetHandlers();
  const widgetCRUDActions = useWidgetCRUDActions();
  const connectionWidgetHandlers = useConnectionWidgetHandlers();
  const automationActions = useAutomationActions();
  const modalActions = useModalActions();

  const { isSmsSupported } = usePhoneNumber();
  const fbPage = useSelector(activeFacebookPageSelector);
  const isSmsActivated = isSmsSupported && fbPage?.smsActivated;
  const isEmailActivated = !!fbPage?.emailActivated;

  const pageCRUDActions = usePageCRUDActions();
  const { isFreeTrialPromotionActive, openFreeTrialUpgradeModalPromotional } =
    useFreeTrialUpgradeModal(fbPage);

  const graphLibInstance = useSelector(getGraphLibInstance);

  const automationAction = useSelector(getAutomationAction);
  const planFeatureFlags = useSelector(combinedFeatureFlagsSelector);
  const hasActivateNumberFlag = useSelector(
    combinedFeatureFlagsSelector,
  ).smsActivateNumber;

  const createConditionPages = async (
    pageGroupId?: number,
    showHidden?: boolean,
  ) => {
    const no = await pageCRUDActions.create(
      {
        name: 'Yes Dialogue',
        pageGroupId,
        botId,
        parentId: campaignId,
        parentType: 'campaign',
      },
      showHidden,
    );

    const yes = await pageCRUDActions.create(
      {
        name: 'No Dialogue',
        botId,
        parentId: campaignId,
        parentType: 'campaign',
      },
      showHidden,
    );
    return { no, yes };
  };

  return useMemo(() => {
    const baseActions = [
      {
        type: WidgetTypes.DELAY,
        id: EAutomationAction.WAIT,
        name: 'Wait',
        icon: DelayIcon,
        iconBgColor: '#FFFBE6',
        iconColor: '#FA8C16',
        add: async (dialogueId: number, sortOrder: number) =>
          delayWidgetHandlers.addNew(dialogueId, sortOrder),
        requiredFeature: 'delayWidget' as const,
      },
      isSmsActivated && {
        type: WidgetTypes.NAVIGATE,
        name: 'Send SMS',
        id: EAutomationAction.SEND_SMS,
        icon: SMSDialogueIcon,
        iconBgColor: '#F6FFED',
        iconColor: '#7CB305',
        add: async (dialogueId, sortOrder, pageGroupId) => {
          if (!hasActivateNumberFlag && !isFreeTrialPromotionActive) {
            openFreeTrialUpgradeModalPromotional();
            return;
          }
          const res = await pageCRUDActions.create({
            name: `SMS Message`,
            botId,
            pageGroupId,
            platforms: ['sms'],
            parentId: campaignId,
            parentType: 'campaign',
          });
          return navigateWidgetHandlers.addNavigate({
            dialogueId,
            sortOrder,
            associatedPageId: res.page.id,
            oppositePageId: null,
            sendNextAfterValue: 5,
            sendNextAfterMeasure: 'minute',
          });
        },
      },
      {
        type: WidgetTypes.NAVIGATE,
        name: 'Send Email',
        id: EAutomationAction.SEND_EMAIL,
        icon: EmailDialogueIcon,
        iconBgColor: '#F9F0FF',
        iconColor: '#9254DE',
        add: async (dialogueId, sortOrder, pageGroupId) => {
          if (!planFeatureFlags.nativeIntegrations) {
            openFreeTrialUpgradeModalPromotional();
            return;
          }
          if (!isEmailActivated) {
            modalActions.openActivateEmailAccountModal();
            return;
          }
          const res = await pageCRUDActions.create({
            name: `Email Message`,
            botId,
            pageGroupId,
            platforms: ['email'],
            parentId: campaignId,
            parentType: 'campaign',
          });
          await widgetCRUDActions.create({
            config: {
              kind: 'to_contact',
            },
            pageId: res.page.id,
            sortOrder: 0,
            type: 'email',
          });
          return navigateWidgetHandlers.addNavigate({
            dialogueId,
            sortOrder,
            associatedPageId: res.page.id,
            oppositePageId: null,
            sendNextAfterValue: 5,
            sendNextAfterMeasure: 'minute',
          });
        },
      },
      {
        type: WidgetTypes.NAVIGATE,
        name: 'Condition',
        id: EAutomationAction.CONDITION,
        icon: ConditionIcon,
        iconBgColor: '#E6F7FF',
        iconColor: '#40A9FF',
        add: async (
          dialogueId: number,
          sortOrder: number,
          pageGroupId,
          widgetId?: number,
          position = 'before' as 'before' | 'after',
        ) => {
          // split existing path
          if (widgetId) {
            try {
              return await widgetCRUDActions.splitPageUsingGoToWidget({
                botId,
                widgetId,
                pageId: dialogueId,
                subtype: 'condition',
                branch: 'yes',
                position,
              });
              // handle later
            } catch (err) {
              console.log('Condition splitPageUsingGoToWidget error: ', err);
            }
            return;
          }

          // Handing cases when no widgets exist, like start of the automation
          const { yes, no } = await createConditionPages(pageGroupId);

          await timeout(500);

          return navigateWidgetHandlers.addNavigate({
            dialogueId,
            sortOrder,
            associatedPageId: yes.page.id,
            oppositePageId: no.page.id,
            sendNextAfterValue: 5,
            sendNextAfterMeasure: 'minute',
          });
        },
      },
      {
        type: WidgetTypes.SEND_DATA,
        id: EAutomationAction.ZAPIER,
        name: 'Zapier',
        description: 'Description',
        icon: ZapierIcon,
        iconBgColor: '#FFF3E6',
        iconColor: '#FF4F00',
        className: 'zapier',
        add: async (dialogueId: number, sortOrder: number) => {
          if (!planFeatureFlags.zapier) {
            openFreeTrialUpgradeModalPromotional();
            return;
          }
          connectionWidgetHandlers.addNew(dialogueId, sortOrder, 'zapier', '');
        },
        requiredFeature: 'zapier' as const,
      },
      {
        type: WidgetTypes.SEND_DATA,
        id: EAutomationAction.JSON_WEBHOOK,
        name: 'JSON Webhook',
        description: 'Description',
        icon: WebhookIcon,
        iconBgColor: '#F0F5FF',
        iconColor: '#40A9FF',
        add: async (dialogueId: number, sortOrder: number) => {
          if (!planFeatureFlags.jsonWebhook) {
            openFreeTrialUpgradeModalPromotional();
            return;
          }
          connectionWidgetHandlers.addNew(dialogueId, sortOrder, 'zapier');
        },
        requiredFeature: 'zapier' as const,
      },
      {
        type: WidgetTypes.SEND_DATA,
        name: 'MailChimp',
        id: EAutomationAction.MAILCHIMP,
        description: 'Description',
        icon: MailchimpIcon,
        iconBgColor: '#FEFFE6',
        iconColor: '#000',
        add: async (dialogueId: number, sortOrder: number) => {
          if (!planFeatureFlags.nativeIntegrations) {
            openFreeTrialUpgradeModalPromotional();
            return;
          }
          connectionWidgetHandlers.addNew(dialogueId, sortOrder, 'mailchimp');
        },
        requiredFeature: 'nativeIntegrations' as const,
      },
      {
        type: WidgetTypes.SEND_DATA,
        name: 'Google Sheet',
        id: EAutomationAction.GOOGLE_SHEET,
        description: 'Description',
        icon: GoogleSheetsIcon,
        iconBgColor: '#F6FFED',
        iconColor: '#389E0D',
        add: async (dialogueId: number, sortOrder: number) => {
          if (!planFeatureFlags.nativeIntegrations) {
            openFreeTrialUpgradeModalPromotional();
            return;
          }
          connectionWidgetHandlers.addNew(
            dialogueId,
            sortOrder,
            'google_sheets',
          );
        },

        requiredFeature: 'nativeIntegrations' as const,
      },
    ].filter(Boolean) as IAutomationAction[];

    const getAllInPath = (
      direction: 'predecessors' | 'successors',
      nodes: string[],
    ) => {
      const allNodes = nodes.reduce((acc, cur) => {
        return [
          ...acc,
          cur,
          ...getAllInPath(
            direction,
            graphLibInstance?.[direction](String(cur)) || [],
          ),
        ];
      }, []);
      return allNodes;
    };

    const self = graphLibInstance?.node(String(props.widgetId));

    const isConditionOrUnicorn =
      self?.data?.widget?.type === 'go_to' &&
      !!self?.data?.widget?.config?.pageId &&
      !!self?.data?.widget?.config?.oppositePageId;

    const successorsToVisitIds =
      props.position === 'before'
        ? [
            String(props.widgetId),
            ...(graphLibInstance?.successors(String(props.widgetId)) || []),
          ]
        : graphLibInstance?.successors(String(props.widgetId)) || [];

    const successorNodesToVisit = successorsToVisitIds
      .map(id => graphLibInstance?.node(id))
      .filter(n => !!n);

    const nodeToFollow = successorNodesToVisit.find(
      n => n?.data?.widget?.pageId === props.dialogueId,
    );

    const selfAndSuccessors = getAllInPath(
      'successors',
      nodeToFollow
        ? [String(nodeToFollow.id as string)]
        : isConditionOrUnicorn
        ? []
        : graphLibInstance?.successors(String(props.widgetId)) || [],
    );

    const hasUnicornsBelow = selfAndSuccessors.reduce((acc, cur) => {
      if (acc) return true;
      const node = graphLibInstance?.node(cur);
      return (
        node?.data?.widget?.type === 'go_to' &&
        node?.data?.widget?.config?.subtype === 'unicorn_detector'
      );
    }, false);

    const findIfInUnicornPath = (nodeToVisitId: string) => {
      const predecessorsIds = graphLibInstance?.predecessors(nodeToVisitId);
      const currentNode = graphLibInstance?.node(nodeToVisitId);

      let isUnicornPath = false;

      // Handle initial case
      if (
        currentNode &&
        nodeToVisitId === String(props.widgetId) &&
        props.position === 'after'
      ) {
        const isCurrentUnicornDetector =
          currentNode?.data?.widget?.type === 'go_to' &&
          currentNode?.data?.widget?.config?.subtype === 'unicorn_detector';

        if (isCurrentUnicornDetector) {
          const isEnteringUnicornPath =
            currentNode?.data?.widget?.config?.pageId === props.dialogueId;

          if (isEnteringUnicornPath) return true;
        }
      }

      if (!currentNode || !predecessorsIds?.length) {
        return isUnicornPath;
      }

      predecessorsIds?.forEach(id => {
        const predecessorNode = graphLibInstance?.node(id);

        const isPredecessorUnicornDetector =
          predecessorNode?.data?.widget?.type === 'go_to' &&
          predecessorNode?.data?.widget?.config?.subtype === 'unicorn_detector';

        if (isPredecessorUnicornDetector) {
          const isEnteringUnicornPath =
            currentNode?.data?.widget?.pageId ===
            predecessorNode?.data?.widget?.config?.pageId;

          if (isEnteringUnicornPath) {
            isUnicornPath = true;
            return;
          }
        }

        const isUnicornUpstream = findIfInUnicornPath(id);

        if (isUnicornUpstream) {
          isUnicornPath = true;
        }
      });

      return isUnicornPath;
    };

    const isInUnicornPath = self?.id ? findIfInUnicornPath(self.id) : false;

    if (isInUnicornPath || props.isUnicornPath) {
      return baseActions;
    }

    const unicornDetectorAction = {
      type: WidgetTypes.NAVIGATE,
      name: 'Unicorn Detector',
      id: EAutomationAction.UNICORN_DETECTOR,
      icon: UnicornDetectorIcon,
      iconBgColor: '#E6F7FF',
      iconColor: '#40A9FF',
      add: async (
        dialogueId: number,
        sortOrder: number,
        pageGroupId,
        widgetId?: number,
        position = 'before' as 'before' | 'after',
      ) => {
        // split existing path
        if (widgetId) {
          try {
            const res = await widgetCRUDActions.splitPageUsingGoToWidget({
              botId,
              widgetId,
              pageId: dialogueId,
              subtype: 'unicorn_detector',
              branch: hasUnicornsBelow ? 'no' : 'yes',
              position,
            });
            return res;
          } catch (err) {
            console.log(
              'Unicorn detector splitPageUsingGoToWidget error: ',
              err,
            );
          }
          return;
        }

        // Handing cases when no widgets exist, like start of the automation
        const { yes, no } = await createConditionPages(pageGroupId, true);

        await timeout(500);

        const nav = await navigateWidgetHandlers.addNavigate({
          dialogueId,
          sortOrder,
          associatedPageId: yes.page.id,
          oppositePageId: no.page.id,
          sendNextAfterValue: 5,
          sendNextAfterMeasure: 'minute',
          subtype: 'unicorn_detector',
        });

        await timeout(500);

        await Promise.all([
          widgetCRUDActions.retrieveAll(yes.page.id),
          widgetCRUDActions.retrieveAll(no.page.id),
        ]);

        return nav;
      },
    } as IAutomationAction;

    return [...baseActions, unicornDetectorAction].filter(action =>
      byVisibility(action.id),
    );
  }, [automationAction, botId, isSmsSupported]);
};
